import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { AppBreadcrumb, AppButton, AppCard, AppContainer } from '@/components/atoms';
import { EditFill } from '@/components/atoms/AppIcon';
import AdminInformationDetail from '@/components/organisms/AdminInformationDetail';
import { useAdminInformationDetail } from '@/hooks/admin/useAdminInformationDetail';
import { ADMIN_ROUTES } from '@/constants/routes';
import { DEFAULT_SITES } from '@/constants/routeSegments';
import { isNotFoundError } from '@/utils/apiError';

// 表示対象 is only visible on super-admin and medix routes (spec SCR-012 #3)
const ROUTES_WITH_TENANT_FIELD = [DEFAULT_SITES.SUPER_ADMIN, DEFAULT_SITES.MEDIX];

const SiteAdminInformationDetailPage: React.FC = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const currentSite = site ?? 'medix';
  const informationId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: detailResponse,
    isLoading,
    isError,
    error,
  } = useAdminInformationDetail(currentSite, informationId);

  const showTenantField = ROUTES_WITH_TENANT_FIELD.includes(
    currentSite as (typeof ROUTES_WITH_TENANT_FIELD)[number]
  );

  const breadcrumbItems = [
    { title: 'お知らせマスタ', href: ADMIN_ROUTES.INFORMATIONS.LIST(currentSite) },
    { title: `詳細:${id}` },
  ];

  if (isLoading) {
    return (
      <AppContainer className="py-6 flex justify-center">
        <Spin size="large" />
      </AppContainer>
    );
  }

  const informationData = detailResponse?.data;

  if (isError && isNotFoundError(error)) {
    return <Navigate to={`/${currentSite}/admin/404`} replace />;
  }

  if (!informationData) {
    return (
      <AppContainer className="py-6">
        <p>お知らせが見つかりません。</p>
      </AppContainer>
    );
  }

  return (
    <AppContainer className="py-6 space-y-6">
      <AppBreadcrumb items={breadcrumbItems} />
      <AppCard title={`お知らせマスタ詳細:${id}`}>
        <AdminInformationDetail data={informationData} showTenantField={showTenantField} />
        <div className="mt-6 flex gap-2">
          <AppButton
            type="primary"
            icon={<EditFill />}
            onClick={() => {
              queryClient.removeQueries({
                queryKey: ['informations', currentSite, informationId],
              });
              navigate(ADMIN_ROUTES.INFORMATIONS.EDIT(currentSite, informationId));
            }}
          >
            編集
          </AppButton>
        </div>
      </AppCard>
    </AppContainer>
  );
};

export default SiteAdminInformationDetailPage;
